import {
  type ConductorTask,
  createForkJoinTask,
  createInlineTask,
  createJoinTask,
  createNoopTask,
  createSimpleTask,
  createSwitchTask,
  createTerminateTask,
  createWaitTask,
  getTaskReferenceName,
  wrapVariable,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import type { Config } from '.'
import { getWaitTimestampExpression } from './helpers/getWaitTimestampExpression'
import { REMINDER_TASK_NAMES, ReminderTaskName } from './reminder.constants'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

type Inputs = 'invoiceId' | 'dueDate' | 'customerId'

const taskReferenceBuilder = getTaskReferenceName<ReminderTaskName>('REMINDER')

export const reminderTaskTransformer = ({
  nodeId,
  config,
  inputMapping
}: {
  nodeId: string
  config: Config
  inputMapping: Record<Inputs, string>
}): ConductorTask[] => {
  const taskReferenceName = taskReferenceBuilder(nodeId)

  /**
   * A point-in-time dunning re-check inserted immediately before a send that follows a human
   * review. `main_valid_for_dunning_check` only guards the send at `until`, before review; the
   * review window is unbounded and the resolution watcher has already timed out by then, so
   * without this an invoice that becomes non-dunnable (paid/void/uncollectible) during review
   * would still be reminded once the reviewer approves. Re-fetches the invoice and terminates the
   * whole workflow if it is no longer valid for dunning; otherwise the switch falls through to the
   * send. The approval-required and replacement-failure review paths are mutually exclusive at
   * build time, so they reuse these reference names.
   */
  const postReviewDunningGuard: ConductorTask[] = [
    createSimpleTask({
      name: 'get_sequence_data',
      taskReferenceName: taskReferenceName(
        REMINDER_TASK_NAMES.postReviewGetInvoice
      ),
      inputParameters: {
        taskExecutorConfiguration: {
          executor: TASK_EXECUTORS.invoice,
          version: 1
        },
        data: wrapVariables({
          invoiceId: inputMapping.invoiceId
        })
      }
    }),
    createSwitchTask({
      name: 'post_review_valid_for_dunning_check',
      taskReferenceName: taskReferenceName(
        REMINDER_TASK_NAMES.postReviewValidForDunningCheck
      ),
      inputParameters: wrapVariables({
        validForDunning: `${taskReferenceName(REMINDER_TASK_NAMES.postReviewGetInvoice)}.output.validForDunning`
      }),
      evaluatorType: 'javascript',
      expression: `(function() { return $.validForDunning === true ? 'CONTINUE' : 'TERMINATE'; })()`,
      decisionCases: {
        TERMINATE: [
          createTerminateTask({
            name: 'post_review_valid_for_dunning_check_terminate',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.postReviewValidForDunningCheckTerminate
            ),
            inputParameters: {
              terminationStatus: 'COMPLETED',
              terminationReason: 'validForDunning is now false'
            }
          })
        ]
      },
      defaultCase: []
    })
  ]

  /**
   * Wraps a before-due reminder's send path in a payment-terms guard. A net-N invoice's due date
   * is N days after it is sent, so a reminder N-or-more days before due fires the moment the
   * invoice is sent — redundant with the invoice email itself. The guard skips the send when the
   * invoice's own payment terms (`paymentTermDays` = dueDate − issueDate, from the invoice executor)
   * don't exceed the reminder offset, and fails open to SEND when either date is unknown. Task
   * names, shape, and expression mirror the backend's `DunningWorkflowPaymentTermsGuard` and
   * `dunning-workflow-definition.json` exactly, so backend-backfilled and UI-published workflows
   * recognise each other as already guarded. An after-due reminder can never fire before its
   * invoice is due, so it is left unguarded.
   */
  const guardSendPathBeforeDue = (
    sendPath: ConductorTask[]
  ): ConductorTask[] => {
    if (config.sendReminder?.type !== 'BEFORE_DUE_DATE') return sendPath

    // Duration.days is optional; default to 0 like getWaitTimestampExpression does, or the
    // expression would read `days > undefined` — false for every invoice, skipping all sends.
    const offsetDays = config.sendReminder.duration.days ?? 0
    return [
      createSwitchTask({
        name: 'main_valid_for_payment_terms_check',
        taskReferenceName: taskReferenceName(
          REMINDER_TASK_NAMES.mainValidForPaymentTermsCheck
        ),
        inputParameters: wrapVariables({
          paymentTermDays: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.paymentTermDays`
        }),
        evaluatorType: 'javascript',
        expression: `(function() { var days = Number($.paymentTermDays); return $.paymentTermDays === null || isNaN(days) || days > ${offsetDays} ? 'SEND' : 'SKIP'; })()`,
        decisionCases: {
          SEND: sendPath
        },
        defaultCase: []
      }),
      /**
       * SWITCH tasks complete as soon as their expression is evaluated, so the main join can't
       * wait on the switch itself. This NOOP only runs once the taken path is done — after the
       * SEND case's tasks, or immediately when the guard skipped the send.
       */
      createNoopTask({
        name: 'payment_terms_check_complete',
        taskReferenceName: taskReferenceName(
          REMINDER_TASK_NAMES.paymentTermsCheckComplete
        ),
        inputParameters: {}
      })
    ]
  }

  return [
    /**
     * Inline task to get the `until` string, which is the timestamp
     * where the core reminder tasks will be executed
     */
    createInlineTask({
      name: 'get_main_wait_until_value',
      taskReferenceName: taskReferenceName(
        REMINDER_TASK_NAMES.getMainWaitUntilValue
      ),
      inputParameters: {
        evaluatorType: 'javascript',
        expression: getWaitTimestampExpression(
          config.sendReminder,
          '$.dueDate'
        ),
        dueDate: wrapVariable(inputMapping.dueDate)
      }
    }),

    createForkJoinTask({
      name: 'main_fork',
      taskReferenceName: taskReferenceName(REMINDER_TASK_NAMES.mainFork),
      forkTasks: [
        /**
         * Main branch: the bulk of the reminder node logic lives here
         */
        [
          /**
           * Wait until the reminder node is triggered, according to the node config.
           * Replacing `until` with `duration` is useful for testing when running locally.
           */
          createWaitTask({
            name: 'main_wait',
            taskReferenceName: taskReferenceName(REMINDER_TASK_NAMES.mainWait),
            inputParameters: {
              until: wrapVariable(
                `${taskReferenceName(REMINDER_TASK_NAMES.getMainWaitUntilValue)}.output.result`
              )
            }
          }),

          /**
           * Get fresh data about the invoice and the customer it's for
           */
          createForkJoinTask({
            name: 'get_data_fork',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.getDataFork
            ),
            forkTasks: [
              [
                createSimpleTask({
                  name: 'get_sequence_data',
                  taskReferenceName: taskReferenceName(
                    REMINDER_TASK_NAMES.getDataInvoice
                  ),
                  inputParameters: {
                    taskExecutorConfiguration: {
                      executor: TASK_EXECUTORS.invoice,
                      version: 1
                    },
                    data: wrapVariables({
                      invoiceId: inputMapping.invoiceId
                    })
                  }
                })
              ],
              [
                createSimpleTask({
                  name: 'get_sequence_data',
                  taskReferenceName: taskReferenceName(
                    REMINDER_TASK_NAMES.getDataCustomer
                  ),
                  inputParameters: {
                    taskExecutorConfiguration: {
                      executor: TASK_EXECUTORS.customer,
                      version: 1
                    },
                    data: wrapVariables({
                      customerId: inputMapping.customerId
                    })
                  }
                })
              ]
            ],
            inputParameters: {}
          }),

          /**
           * Wait for the invoice and customer data
           */
          createJoinTask({
            name: 'get_data_join',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.getDataJoin
            ),
            joinOn: [
              taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice),
              taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)
            ],
            inputParameters: {}
          }),

          /**
           * Check if `validForDunning` has changed since the loop check was last run
           */
          ...guardSendPathBeforeDue([
            createSwitchTask({
              name: 'main_valid_for_dunning_check',
              taskReferenceName: taskReferenceName(
                REMINDER_TASK_NAMES.mainValidForDunningCheck
              ),
              inputParameters: wrapVariables({
                validForDunning: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.validForDunning`
              }),
              evaluatorType: 'javascript',
              expression: `(function() { return $.validForDunning === true ? 'CONTINUE' : 'TERMINATE'; })()`,
              decisionCases: {
                TERMINATE: [
                  createTerminateTask({
                    name: 'main_valid_for_dunning_check_terminate',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.mainValidForDunningCheckTerminate
                    ),
                    inputParameters: {
                      terminationStatus: 'COMPLETED',
                      terminationReason: 'validForDunning is now false'
                    }
                  })
                ]
              },
              defaultCase: []
            }),

            ...(config.isApprovalRequired
              ? [
                  /**
                   * Create the manual task for this message to be reviewed.
                   */
                  createSimpleTask({
                    name: 'manual_task',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.manualReview
                    ),
                    inputParameters: {
                      taskExecutorConfiguration: {
                        executor: TASK_EXECUTORS.reviewEmail,
                        version: 1
                      },
                      data: {
                        to: config.to,
                        cc: config.cc,
                        bcc: config.bcc,
                        subject: config.subject,
                        text: config.template,
                        ...wrapVariables({
                          invoice: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.invoice`,
                          customer: `${taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)}.output.customer`,
                          invoiceNumber: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.invoice.invoiceNumber`,
                          customerName: `${taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)}.output.customer.legalName`
                        }),
                        assignmentRules: {
                          type: 'users',
                          users: config.approverUserIds
                        }
                      }
                    }
                  }),
                  /**
                   * Re-check the invoice is still dunnable now review is done, before sending.
                   */
                  ...postReviewDunningGuard,
                  /**
                   * Send the email with the values provided by the manual task output. The manual review processor
                   * is responsible for replacing Handlebars variables.
                   */
                  createSimpleTask({
                    name: 'email',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.sendInvoiceReminder
                    ),
                    inputParameters: {
                      taskExecutorConfiguration: {
                        executor: TASK_EXECUTORS.sendInvoiceReminder,
                        version: 1
                      },
                      ...wrapVariables({
                        to: `${taskReferenceName(REMINDER_TASK_NAMES.manualReview)}.output.to`,
                        cc: `${taskReferenceName(REMINDER_TASK_NAMES.manualReview)}.output.cc`,
                        bcc: `${taskReferenceName(REMINDER_TASK_NAMES.manualReview)}.output.bcc`,
                        subject: `${taskReferenceName(REMINDER_TASK_NAMES.manualReview)}.output.subject`,
                        text: `${taskReferenceName(REMINDER_TASK_NAMES.manualReview)}.output.text`,
                        invoiceId: inputMapping.invoiceId
                      })
                    }
                  })
                ]
              : [
                  /**
                   * When sending this email without review, process the Handlebars values from the fields and template
                   */
                  createSimpleTask({
                    name: 'email',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.generateInvoiceReminder
                    ),
                    inputParameters: {
                      taskExecutorConfiguration: {
                        executor: TASK_EXECUTORS.generateInvoiceReminder,
                        version: 1
                      },
                      configuration: {
                        to: config.to,
                        cc: config.cc,
                        bcc: config.bcc,
                        subject: config.subject,
                        text: config.template
                      },
                      ...wrapVariables({
                        invoice: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.invoice`,
                        customer: `${taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)}.output.customer`
                      })
                    }
                  }),
                  /**
                   * Check the `replacementFailure` value from the `generate_invoice_reminder` task and create a
                   * manual review task if it returns true
                   */
                  createSwitchTask({
                    name: 'replacement_failure_switch',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.replacementFailureSwitch
                    ),
                    inputParameters: wrapVariables({
                      replacementFailure: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.replacementFailure`
                    }),
                    evaluatorType: 'javascript',
                    expression: `(function() { return $.replacementFailure === true ? 'REVIEW' : 'SEND'; })()`,
                    decisionCases: {
                      REVIEW: [
                        createSimpleTask({
                          name: 'manual_task',
                          taskReferenceName: taskReferenceName(
                            REMINDER_TASK_NAMES.replacementFailureManualReview
                          ),
                          inputParameters: {
                            taskExecutorConfiguration: {
                              executor: TASK_EXECUTORS.reviewEmail,
                              version: 1
                            },
                            data: {
                              to: config.to,
                              cc: config.cc,
                              bcc: config.bcc,
                              subject: config.subject,
                              text: config.template,
                              ...wrapVariables({
                                invoice: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.invoice`,
                                customer: `${taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)}.output.customer`,
                                invoiceNumber: `${taskReferenceName(REMINDER_TASK_NAMES.getDataInvoice)}.output.invoice.invoiceNumber`,
                                customerName: `${taskReferenceName(REMINDER_TASK_NAMES.getDataCustomer)}.output.customer.legalName`
                              }),
                              /**
                               * There are no manually selected users for this review task, so send them to roles instead
                               */
                              assignmentRules: {
                                type: 'role',
                                roles: ['finance', 'admin']
                              }
                            }
                          }
                        }),
                        /**
                         * Re-check the invoice is still dunnable now review is done, before sending.
                         */
                        ...postReviewDunningGuard,
                        createSimpleTask({
                          name: 'email',
                          taskReferenceName: taskReferenceName(
                            REMINDER_TASK_NAMES.replacementFailureSendInvoiceReminderAfterReview
                          ),
                          inputParameters: {
                            taskExecutorConfiguration: {
                              executor: TASK_EXECUTORS.sendInvoiceReminder,
                              version: 1
                            },
                            ...wrapVariables({
                              to: `${taskReferenceName(REMINDER_TASK_NAMES.replacementFailureManualReview)}.output.to`,
                              cc: `${taskReferenceName(REMINDER_TASK_NAMES.replacementFailureManualReview)}.output.cc`,
                              bcc: `${taskReferenceName(REMINDER_TASK_NAMES.replacementFailureManualReview)}.output.bcc`,
                              subject: `${taskReferenceName(REMINDER_TASK_NAMES.replacementFailureManualReview)}.output.subject`,
                              text: `${taskReferenceName(REMINDER_TASK_NAMES.replacementFailureManualReview)}.output.text`,
                              invoiceId: inputMapping.invoiceId
                            })
                          }
                        })
                      ],
                      SEND: [
                        createSimpleTask({
                          name: 'email',
                          taskReferenceName: taskReferenceName(
                            REMINDER_TASK_NAMES.sendInvoiceReminderWithoutFailureReview
                          ),
                          inputParameters: {
                            taskExecutorConfiguration: {
                              executor: TASK_EXECUTORS.sendInvoiceReminder,
                              version: 1
                            },
                            ...wrapVariables({
                              to: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.to`,
                              cc: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.cc`,
                              bcc: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.bcc`,
                              subject: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.subject`,
                              text: `${taskReferenceName(REMINDER_TASK_NAMES.generateInvoiceReminder)}.output.text`,
                              invoiceId: inputMapping.invoiceId
                            })
                          }
                        })
                      ]
                    },
                    defaultCase: []
                  }),
                  /**
                   * SWITCH tasks complete as soon as their expression is evaluated, so the
                   * main join can't wait on the switch itself. This NOOP is only scheduled
                   * after the selected decision case finishes, giving the join a reference
                   * that means "reminder sent (reviewed if necessary)".
                   */
                  createNoopTask({
                    name: 'replacement_failure_complete',
                    taskReferenceName: taskReferenceName(
                      REMINDER_TASK_NAMES.replacementFailureComplete
                    ),
                    inputParameters: {}
                  })
                ])
          ])
        ],
        /**
         * Watcher branch: a single event-driven WAIT (no timer), parallel to the reminder branch.
         * The platform completes it on a terminal invoice event (paid / void / uncollectible), so it
         * stays parked for the whole node — including any manual-review window — rather than expiring
         * at the send time. On completion it re-fetches the invoice and, if no longer valid for
         * dunning, cancels any pending review and terminates the whole workflow — stopping this
         * reminder and every later one. With no terminal event it never fires and is cancelled when
         * the send completes the workflow. Replaces the old polling do-while.
         */
        [
          createWaitTask({
            name: 'invoice_resolution_wait',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.invoiceResolutionWait
            ),
            inputParameters: {}
          }),
          /**
           * Re-fetch the invoice — backstop in case the completing event was not actually terminal.
           */
          createSimpleTask({
            name: 'get_sequence_data',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.invoiceResolutionGetInvoice
            ),
            inputParameters: {
              taskExecutorConfiguration: {
                executor: TASK_EXECUTORS.invoice,
                version: 1
              },
              data: wrapVariables({
                invoiceId: inputMapping.invoiceId
              })
            }
          }),
          /**
           * If the invoice is no longer valid for dunning, cancel any pending review and terminate.
           * Otherwise (woke on its timer with the invoice still dunnable) do nothing and let the node
           * complete.
           */
          createSwitchTask({
            name: 'invoice_resolution_check',
            taskReferenceName: taskReferenceName(
              REMINDER_TASK_NAMES.invoiceResolutionCheck
            ),
            inputParameters: wrapVariables({
              validForDunning: `${taskReferenceName(REMINDER_TASK_NAMES.invoiceResolutionGetInvoice)}.output.validForDunning`
            }),
            evaluatorType: 'javascript',
            expression: `(function() { return $.validForDunning === true ? 'CONTINUE' : 'TERMINATE'; })()`,
            decisionCases: {
              TERMINATE: [
                createSimpleTask({
                  name: 'cancel_manual_task',
                  taskReferenceName: taskReferenceName(
                    REMINDER_TASK_NAMES.invoiceResolutionCancelManualTask
                  ),
                  inputParameters: {
                    taskExecutorConfiguration: {
                      version: 1,
                      executor: TASK_EXECUTORS.cancelManualTask
                    },
                    data: wrapVariables({
                      workflowId: 'workflow.workflowId'
                    })
                  }
                }),
                createTerminateTask({
                  name: 'invoice_resolution_terminate',
                  taskReferenceName: taskReferenceName(
                    REMINDER_TASK_NAMES.invoiceResolutionTerminate
                  ),
                  inputParameters: {
                    terminationStatus: 'COMPLETED',
                    terminationReason: 'validForDunning is now false'
                  }
                })
              ]
            },
            defaultCase: []
          })
        ]
      ],
      inputParameters: {}
    }),

    /**
     * Join the main fork so branching works correctly
     */
    createJoinTask({
      name: 'main_join',
      taskReferenceName: taskReferenceName(REMINDER_TASK_NAMES.mainJoin),
      joinOn:
        config.sendReminder?.type === 'BEFORE_DUE_DATE'
          ? [taskReferenceName(REMINDER_TASK_NAMES.paymentTermsCheckComplete)]
          : config.isApprovalRequired
            ? [taskReferenceName(REMINDER_TASK_NAMES.sendInvoiceReminder)]
            : [
                taskReferenceName(
                  REMINDER_TASK_NAMES.replacementFailureComplete
                )
              ],
      inputParameters: {}
    })
  ]
}
